import { apiGet, apiPut, apiDelete, apiPost } from '@/lib/api';
import { Conversation, Message, TreeNode, Client } from '@/types';
/**
 * Conversation Service
 * 处理所有会话相关的API调用
 */
// Note: response interfaces removed because this module now uses `fetchJson` and
// the typed axios response generics are no longer required here.
/**
 * 获取会话列表
 */
// Safe date parser: preserves backend timestamps; falls back to now if invalid
function safeParseDate(value: any): string {
  if (!value) return new Date().toISOString();
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  } catch {
    return new Date().toISOString();
  }
}

export async function fetchConversations(
  shareConversation?: string | null,
  projectId?: string | null
): Promise<Conversation[]> {
  const params: any = {};
  if (projectId) {
    params.projectId = projectId;
    // Backend may default to a small page size for project-scoped queries.
    // Request a larger page explicitly to avoid silently truncating the list.
    Object.assign(params, {
      page: '1',
      pageSize: '1000',
    });
  } else {
    Object.assign(params, {
      page: '1',
      pageSize: '30',
      allProjects: 'true',
    });
  }
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach(k => searchParams.append(k, String(params[k])));

  const res = await apiGet<any>(
    `/api/ai/conversations?${searchParams.toString()}`
  );
  const data = res?.data || res || {};
  const resConversation = data.conversations || [];
  const conversationsData = shareConversation
    ? resConversation.filter(
        (it: any) => it.conversationId === shareConversation
      )
    : data.conversations;
  // Transform API data to local format
  const transformedConversations: Conversation[] = conversationsData.map(
    (conv: any) => {
      return {
        id: conv.conversationId,
        conversationId: conv.conversationId,
        title: conv.title,
        treeData: [], // ✅ 初始化为空数组
        versionPath: {}, // ✅ 初始化为空对象
        // Use backend timestamps if provided; fallback gracefully
        createdAt: safeParseDate(conv.createdAt),
        updatedAt: safeParseDate(conv.updatedAt || conv.createdAt),
        projectId: conv.projectId || projectId, // 保留 projectId
        starred: conv.starred || false, // 添加 starred 字段
        conversationType: conv.conversationType || 'single',
        lastVisitedAt: conv.lastVisitedAt ?? null, // 🔥 添加 lastVisitedAt 用于群聊未读状态判断
      };
    }
  );
  return transformedConversations;
}
/**
 * 🔥 遍历树形数据，处理已取消的消息
 * 1. 以 id 为 key 构建全局 map
 * 2. 找到有 cancelledMessageId 的节点，将对应的被取消节点标记 isCanceled: true
 */
export function processCancelledMessages(treeData: TreeNode[]): void {
  const nodeMap = new Map<number, TreeNode & { isCanceled?: boolean }>();

  // 第一遍：构建所有节点的 map
  function buildNodeMap(nodes: TreeNode[]) {
    nodes.forEach(node => {
      nodeMap.set(node.id, node as any);
      if (node.children && node.children.length > 0) {
        buildNodeMap(node.children);
      }
    });
  }

  // 第二遍：找到有 cancelledMessageId 的节点，标记被取消的节点
  function markCancelledNodes(nodes: TreeNode[]) {
    nodes.forEach(node => {
      const nodeData = node as any;
      // 检查是否有 cancelledMessageId（取消动作对应的原消息 ID）
      if (
        nodeData.cancelledMessageId &&
        typeof nodeData.cancelledMessageId === 'number'
      ) {
        const cancelledNode = nodeMap.get(nodeData.cancelledMessageId);
        if (cancelledNode) {
          cancelledNode.isCanceled = true;
        }
      }
      if (node.children && node.children.length > 0) {
        markCancelledNodes(node.children);
      }
    });
  }

  buildNodeMap(treeData);
  markCancelledNodes(treeData);
}

/**
 * 🔥 将一维数组转换为树形结构
 * 使用 previousMessageId 和 children 字段构建树
 */
function buildTreeFromFlatArray(flatArray: any[]): TreeNode[] {
  if (!flatArray || flatArray.length === 0) return [];

  // 创建 ID 到节点的映射
  const nodeMap = new Map<number, TreeNode>();

  // 第一步：创建所有节点的映射
  flatArray.forEach(item => {
    const node: TreeNode = {
      ...item,
      children: [], // 初始化为空数组
    };
    nodeMap.set(item.id, node);
  });

  // 第二步：构建树结构
  const roots: TreeNode[] = [];

  flatArray.forEach(item => {
    const node = nodeMap.get(item.id);
    if (!node) return;

    if (
      item.previousMessageId === null ||
      item.previousMessageId === undefined
    ) {
      // 没有父节点的是根节点
      roots.push(node);
    } else {
      // 找到父节点并添加到其 children 中
      const parent = nodeMap.get(item.previousMessageId);
      if (parent) {
        // 确保 parent.children 已初始化
        if (!parent.children) {
          parent.children = [];
        }
        // 确保子节点按照 children 数组中的顺序排列
        if (item.children && Array.isArray(item.children)) {
          // 使用 children 数组中的 ID 顺序来排序同级节点
          const siblings = flatArray.filter(
            n => n.previousMessageId === item.previousMessageId
          );
          const index = siblings.findIndex(n => n.id === item.id);
          if (index >= 0 && parent.children) {
            parent.children[index] = node;
          } else if (parent.children) {
            parent.children.push(node);
          }
        } else if (parent.children) {
          parent.children.push(node);
        }
      }
    }
  });

  // 第三步：对每个节点的 children 进行排序（基于原始数据中的 children 数组顺序）
  flatArray.forEach(item => {
    const node = nodeMap.get(item.id);
    if (
      node &&
      node.children &&
      node.children.length > 0 &&
      item.children &&
      Array.isArray(item.children)
    ) {
      // 根据原始 children ID 数组的顺序排序
      const childrenIds = item.children;
      node.children.sort((a: TreeNode, b: TreeNode) => {
        const indexA = childrenIds.indexOf(a.id);
        const indexB = childrenIds.indexOf(b.id);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }
  });

  // 清理空的 children 数组
  function cleanEmptyChildren(nodes: TreeNode[]) {
    nodes.forEach(node => {
      if (node.children && node.children.length === 0) {
        node.children = [];
      } else if (node.children && node.children.length > 0) {
        cleanEmptyChildren(node.children);
      }
    });
  }

  cleanEmptyChildren(roots);

  return roots;
}

/**
 * 获取会话详情（包括消息历史）
 * 新版本：只返回树形数据和默认的版本路径
 * - 树的深度表示对话的继续（user -> assistant -> user -> assistant...）
 * - 树的广度（同级children）表示当前消息的不同版本
 * - 默认选择每层的第一个版本（children[0]）
 *
 * @returns { treeData, versionPath } - treeData: 原始树形数据, versionPath: 默认版本路径
 */
export async function fetchConversationDetails(
  conversationId: string
): Promise<{
  treeData: TreeNode[];
  versionPath: Record<number, number>;
  conversationType?: 'single' | 'group';
  title?: string;
  projectId?: string | null;
}> {
  const res = await apiGet<any>(`/api/ai/conversations/${conversationId}`);
  const data = res?.data || res || {};
  const historiesFlat = data.histories || [];

  // 🔥 新增：将一维数组转换为树形结构
  const histories = buildTreeFromFlatArray(historiesFlat);

  // 🔥 处理已取消的消息（标记 isCanceled 属性）
  processCancelledMessages(histories);

  // 构建默认的 versionPath（每层都选择第一个版本）
  const versionPath = buildDefaultVersionPath(histories);

  return {
    treeData: histories,
    versionPath,
    conversationType: data.conversationType, // 🔥 返回会话类型，用于判断群聊
    title: data.title,
    projectId: data.projectId,
  };
}

/**
 * 获取“别人分享给我”的会话详情（只读）
 * API: GET /api/ai/conversations/shared/{shared_conversation_id}
 */
export async function fetchSharedConversationDetails(
  sharedConversationId: number | string
): Promise<{
  conversationId: string;
  title: string;
  treeData: TreeNode[];
  versionPath: Record<number, number>;
}> {
  const res = await apiGet<any>(
    `/api/ai/conversations/shared/${sharedConversationId}`
  );
  const data = res?.data || res || {};

  // Be defensive: backend response shape may vary.
  const conversationId = String(
    data.conversationId ?? data.conversation?.conversationId ?? ''
  ).trim();
  const title = String(data.title ?? data.conversation?.title ?? 'Shared');
  const historiesFlat: any[] =
    data.histories ?? data.conversation?.histories ?? data.treeData ?? [];

  // 🔥 新增：将一维数组转换为树形结构
  const histories = buildTreeFromFlatArray(historiesFlat);

  // 🔥 处理已取消的消息（标记 isCanceled 属性）
  processCancelledMessages(histories);

  const versionPath = buildDefaultVersionPath(histories);

  return {
    conversationId: conversationId || String(sharedConversationId),
    title,
    treeData: histories,
    versionPath,
  };
}
/**
 * 构建默认的版本路径（每层都选择第一个版本）
 */
function buildDefaultVersionPath(
  treeData: TreeNode[],
  currentPath: Record<number, number> = {}
): Record<number, number> {
  const versionPath: Record<number, number> = { ...currentPath };

  function traverse(nodes: TreeNode[], depth: number) {
    if (!nodes || nodes.length === 0) return;

    // 默认选择第一个版本
    versionPath[depth] = 0;

    // 递归处理下一层
    const firstNode = nodes[0];
    if (firstNode.children && firstNode.children.length > 0) {
      traverse(firstNode.children, depth + 1);
    }
  }

  if (treeData && treeData.length > 0) {
    traverse(treeData, 0);
  }

  return versionPath;
}

/**
 * 从 treeData 和 versionPath 获取激活路径的最后一条消息的 backendId
 */
export function getLastActiveMessageId(
  treeData: TreeNode[],
  versionPath: Record<number, number>
): number | undefined {
  if (!treeData || treeData.length === 0) return undefined;

  let currentNodes = treeData;
  let depth = 0;
  let lastBackendId: number | undefined = undefined;

  while (currentNodes && currentNodes.length > 0) {
    // 获取当前层激活的版本索引
    const selectedVersion = versionPath[depth] ?? 0;
    const selectedNode = currentNodes[selectedVersion] || currentNodes[0];

    if (selectedNode) {
      lastBackendId = selectedNode.id; // 保存当前节点的 backendId

      // 继续到下一层
      currentNodes = selectedNode.children || [];
      depth++;
    } else {
      break;
    }
  }

  return lastBackendId;
}

/**
 * 从 treeData 和 versionPath 获取激活路径中最后一个包含 client 的节点信息
 */
export function getLastClientFromTreeData(
  treeData: TreeNode[],
  versionPath: Record<number, number>
): Client | undefined {
  if (!treeData || treeData.length === 0) return undefined;

  let currentNodes = treeData;
  let depth = 0;
  let lastClient: Client | undefined = undefined;

  while (currentNodes && currentNodes.length > 0) {
    // 获取当前层激活的版本索引
    const selectedVersion = versionPath[depth] ?? 0;
    const selectedNode = currentNodes[selectedVersion] || currentNodes[0];

    if (selectedNode) {
      // 如果当前节点有 client，更新 lastClient
      if (selectedNode.client) {
        lastClient = selectedNode.client;
      }

      // 继续到下一层
      currentNodes = selectedNode.children || [];
      depth++;
    } else {
      break;
    }
  }

  return lastClient;
}
/**
 * 根据版本路径重新构建消息数组
 * @param treeData 原始树形数据
 * @param conversationId 对话ID
 * @param versionPath 版本路径，例如 {0: 0, 1: 1} 表示第0层选择版本0，第1层选择版本1
 * @returns 重新构建的消息数组
 */
export function rebuildMessagesWithVersionPath(
  treeData: TreeNode[],
  conversationId: string,
  versionPath: Record<number, number>
): Message[] {
  const messages: Message[] = [];

  function traverseLevel(nodes: TreeNode[], depth: number) {
    if (!nodes || nodes.length === 0) return;

    // 确定role
    const firstNode = nodes[0];
    const role = firstNode?.role || 'assistant';
    // 将同级nodes作为该消息的不同版本（choices）
    const choices = nodes.map((node, idx) => {
      const blocks = convertContentsToBlocks(node.contents, node.id);
      return {
        version: idx,
        blocks: blocks,
        tokens: (node as any)?.tokens ?? 0,
        inputTokens: (node as any)?.inputTokens ?? 0,
      };
    });

    // 获取当前层应该选择的版本索引
    const selectedVersion = versionPath[depth] ?? 0;
    const selectedNode = nodes[selectedVersion] || nodes[0];

    // 创建Message对象（精简版）
    // 🔥 修复：优先使用 backendId（流式渲染时设置），否则使用 id（API 返回的数字 ID）
    // 流式渲染时 node.id 是 pending 字符串，backendId 是 message_init 返回的数字 ID
    // 🔥 类型断言：TreeNode 类型定义中没有 backendId，但运行时会动态添加
    const resolvedBackendId =
      typeof (selectedNode as any).backendId === 'number'
        ? (selectedNode as any).backendId
        : typeof selectedNode.id === 'number'
          ? selectedNode.id
          : undefined;

    const message: Message = {
      id: `${conversationId}-${depth}`,
      backendId: resolvedBackendId,
      previousMessageId: selectedNode.previousMessageId,
      choices: choices,
      role: role,
      client: selectedNode.client || undefined,
      timestamp: new Date(selectedNode.createdAt || Date.now()),
      isStreaming: selectedNode.isStreaming || false,
      score: selectedNode.score || 0,
      tokens:
        (selectedNode as any)?.tokens ?? choices[selectedVersion]?.tokens ?? 0,
      inputTokens:
        (selectedNode as any)?.inputTokens ??
        choices[selectedVersion]?.inputTokens ??
        0,
    };

    // 🔥 群聊：传递 user 信息用于区分消息发送者
    if (selectedNode.user) {
      (message as any).user = selectedNode.user;
    }

    messages.push(message);

    // 递归处理下一层（使用选中版本的children）
    const nextNodes = selectedNode.children;
    if (nextNodes && nextNodes.length > 0) {
      traverseLevel(nextNodes, depth + 1);
    }
  }

  if (treeData && treeData.length > 0) {
    traverseLevel(treeData, 0);
  }

  return messages;
}
/**
 * 将API的contents数组转换为MessageBlock数组
 */
function convertContentsToBlocks(contents: any[], nodeId: number): any[] {
  if (!contents || contents.length === 0) return [];
  return contents.map((item: any, idx: number) => {
    let itemContent = item.content;
    if (item.type === 'ref') {
      // 引用类型：添加可点击的PDF链接
      const url = `${window.location.origin}${encodeURIComponent(`/api/files/retrieve?filePath=${item.file}&folderID=${item.folderID}`)}${item.page ? `#page=${item.page}` : ''}`;
      const htmlLink = `[${item.order}] <a href="#" data-pdf-url="${url}" data-pdf-filename="${item.file}" style="color: #0066cc; text-decoration: underline; cursor: pointer;">${extractFileName(item.file)}</a>`;
      itemContent = htmlLink;
    }
    // 对于文件类型，保留原始的 id；其他类型使用 nodeId-idx 格式
    const blockId = item.type === 'file' ? String(item.id) : `${nodeId}-${idx}`;
    const block: any = {
      type: item.type || 'text',
      id: blockId,
      content: String(itemContent),
      collapsed: item.type === 'think' ? true : false,
    };

    // 对于文件类型，保留 fileId 属性
    // 🔥 修复：历史消息中可能使用不同字段存储文件ID
    // 优先级：fileId > id（数据库ID，有些情况下与文件ID相同）> content
    if (item.type === 'file') {
      block.fileId = item.fileId || item.id || item.content || null;
    }

    return block;
  });
}
/**
 * 重命名会话
 */
export async function renameConversation(
  conversationId: string,
  title: string
): Promise<any> {
  return await apiPut(`/api/ai/conversations/${conversationId}`, { title });
}

/**
 * 删除单个会话
 */
export async function deleteConversation(conversationId: string): Promise<any> {
  return await apiDelete(`/api/ai/conversations/${conversationId}`);
}

/**
 * 更新会话（重命名或删除）
 * @deprecated 请使用 renameConversation 或 deleteConversation
 */
export async function updateConversation(
  conversationId: string,
  method: 'PUT' | 'DELETE',
  title?: string
): Promise<any> {
  if (method === 'PUT' && title) {
    return renameConversation(conversationId, title);
  } else if (method === 'DELETE') {
    return deleteConversation(conversationId);
  }
}
/**
 * 删除会话中的某个session
 */
export async function deleteConversationSession(
  conversationId: string,
  messagesId: string
): Promise<void> {
  await apiDelete(
    `/api/ai/conversations/${conversationId}/messages/${messagesId}`
  );
}

/**
 * 获取问题类型列表
 */
export async function fetchIssueTypes(): Promise<
  { value: string; label: string }[]
> {
  const res = await apiGet<any>('/api/ai/conversations/messages/issue-types');
  return res?.data || res || [];
}

/**
 * 提交消息反馈评分
 */
export async function submitMessageFeedback(
  conversationId: string,
  messageId: number,
  score: number,
  feedback: string,
  issueTypes: string
): Promise<void> {
  await apiPost(
    `/api/ai/conversations/${conversationId}/messages/${messageId}/feedback`,
    {
      score,
      feedback: feedback ? feedback : null,
      issueType: issueTypes ? issueTypes : null,
    }
  );
}

/**
 * 取消消息
 * @param messageId 要取消的消息 ID
 * @param previousMessageId 前一条消息的 ID
 * @returns 取消后的消息信息（用于构建 cancel 类型节点）
 */
export async function cancelMessage(
  messageId: number,
  previousMessageId: number
): Promise<{
  messageId: number;
  assistantMessageId: number; // 🔥 新增：API 返回的 assistant 消息 ID
  conversationId: string;
  previousMessageId: number;
  content: string;
  userId: number;
  client?: {
    id: number | string;
    name: string;
    logo?: string;
  };
}> {
  return await apiPost(`/api/clients/chat/messages/${messageId}/cancel`, {
    previousMessageId,
  });
}

// Helper function
function extractFileName(filePath: string): string {
  if (!filePath) return '';
  const parts = filePath.split(/[/\\]/);
  return parts[parts.length - 1] || filePath;
}
